import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface SelectFilterOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SelectFilterProps<T extends string = string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SelectFilterOption<T>[];
  /** Accessible label for the trigger. */
  ariaLabel: string;
  placeholder?: string;
  className?: string;
}

/**
 * A compact, shadcn-styled filter dropdown for a table toolbar. Replaces raw
 * native `<select>` filter controls so every list screen looks consistent.
 * Generic over the option value type so callers keep their literal union
 * (no `as` casts) — the single string→T cast is centralized here, since
 * Radix's `onValueChange` only knows `string`.
 */
export function SelectFilter<T extends string = string>({
  value,
  onValueChange,
  options,
  ariaLabel,
  placeholder,
  className,
}: SelectFilterProps<T>) {
  return (
    <Select value={value} onValueChange={(next) => onValueChange(next as T)}>
      <SelectTrigger
        aria-label={ariaLabel}
        size="sm"
        className={cn("w-auto min-w-36", className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
